
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cerrar menú móvil al cambiar de página
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Bloquear scroll cuando el menú está abierto
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Empresa', path: '/empresa' },
    { name: 'Servicios', path: '/servicios' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contacto', path: '/contacto' },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        {/* TopBar */}
        <div className={`bg-dark-blue text-white py-2 sm:py-2.5 transition-all duration-300 ${isScrolled ? 'hidden' : 'block'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-6 text-xs sm:text-sm">
                <a
                  href="tel:+34629501941"
                  className="flex items-center space-x-2 hover:text-primary transition-colors duration-300"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-sm"></i>
                  </div>
                  <span>+34 629 501 941</span>
                </a>
                <a
                  href="mailto:info@pladurex.es"
                  className="flex items-center space-x-2 hover:text-primary transition-colors duration-300"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-sm"></i>
                  </div>
                  <span className="hidden xs:inline">info@pladurex.es</span>
                </a>
                <div className="hidden md:flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-sm"></i>
                  </div>
                  <span>Lun - Vie: 08:00 - 18:00</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <a
                  href="#"
                  className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-all duration-300"
                  aria-label="Facebook"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-facebook-fill text-xs"></i>
                  </div>
                </a>
                <a
                  href="#"
                  className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-all duration-300"
                  aria-label="Instagram"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-instagram-line text-xs"></i>
                  </div>
                </a>
                <a
                  href="#"
                  className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-all duration-300"
                  aria-label="LinkedIn"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-linkedin-fill text-xs"></i>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className={`transition-all duration-300 ${isScrolled ? 'bg-white' : 'bg-white/95 backdrop-blur-sm'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-4">
              <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                <img 
                  src="https://public.readdy.ai/ai/img_res/1029ff3f-3984-48ef-bfe7-a19b1b30976d.png" 
                  alt="PLADUREX Logo" 
                  className="h-10 sm:h-12 w-auto"
                />
                <span className="text-xl sm:text-2xl font-bold text-dark-blue">PLADUREX</span>
              </Link>

              <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-sm lg:text-[15px] font-medium transition-colors duration-300 hover:text-primary whitespace-nowrap ${
                      location.pathname === item.path
                        ? 'text-primary'
                        : 'text-text-dark'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>

              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <a
                  href="tel:+34629501941"
                  className="hidden sm:flex items-center space-x-2 px-4 lg:px-5 py-2 lg:py-2.5 border-2 border-primary text-primary rounded-full font-medium text-xs lg:text-[14px] transition-all duration-300 hover:bg-primary hover:text-white hover:shadow-lg whitespace-nowrap"
                >
                  <div className="w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-base lg:text-[18px]"></i>
                  </div>
                  <span>Llamar</span>
                </a>
                <a
                  href="https://wa.me/34629501941"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 lg:px-5 py-2 lg:py-2.5 bg-[#25D366] text-white rounded-full font-medium text-xs lg:text-[14px] transition-all duration-300 hover:bg-[#20BA5A] hover:shadow-lg whitespace-nowrap"
                >
                  <div className="w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-base lg:text-[18px]"></i>
                  </div>
                  <span className="hidden xs:inline">WhatsApp</span>
                </a>
                
                {/* Botón hamburguesa móvil */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-300 cursor-pointer"
                  aria-label="Abrir menú"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-text-dark`}></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Menú móvil desplegable */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Panel del menú */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 right-0 h-full w-[280px] bg-white z-50 md:hidden shadow-2xl"
            >
              <div className="flex flex-col h-full">
                {/* Cabecera del menú */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <span className="text-lg font-bold text-dark-blue">Menú</span>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors duration-300 cursor-pointer"
                    aria-label="Cerrar menú"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-close-line text-xl text-text-dark"></i>
                    </div>
                  </button>
                </div>
                
                {/* Enlaces de navegación */}
                <nav className="flex-1 py-4">
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={item.path}
                        className={`flex items-center px-6 py-4 text-base font-medium transition-colors duration-300 ${
                          location.pathname === item.path
                            ? 'text-primary bg-primary/5 border-r-4 border-primary'
                            : 'text-text-dark hover:text-primary hover:bg-gray-50'
                        }`}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  ))}
                </nav>
                
                {/* Botones de contacto */}
                <div className="p-4 border-t border-gray-100 space-y-3">
                  <a
                    href="tel:+34629501941"
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3 border-2 border-primary text-primary rounded-full font-medium text-sm transition-all duration-300 hover:bg-primary hover:text-white"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <span>Llamar ahora</span>
                  </a>
                  <a
                    href="https://wa.me/34629501941"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-[#25D366] text-white rounded-full font-medium text-sm transition-all duration-300 hover:bg-[#20BA5A]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-lg"></i>
                    </div>
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
